import { Button } from "@/components/ui/button";
import { ExcursionCard } from "@/components/ExcursionCard";
import { TestimonialCard } from "@/components/TestimonialCard";
import { UniversalImageManager } from "@/components/UniversalImageManager";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, type CarouselApi } from "@/components/ui/carousel";
import { useExcursions } from "@/hooks/useExcursions";
import { useTestimonials } from "@/hooks/useTestimonials";
import { usePhotoGalleries } from "@/hooks/usePhotoGalleries";
import { useSiteImages } from "@/hooks/useSiteImages";
import { ArrowRight, Heart, Users, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { EditableImage } from "@/components/EditableImage";

export const HomepagePreview = () => {
  const { testimonials } = useTestimonials(false);
  const { excursions } = useExcursions();
  const { galleries, updateGallery } = usePhotoGalleries();
  const { images: siteImages } = useSiteImages();

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [solidaireCarouselApi, setSolidaireCarouselApi] = useState<CarouselApi>();
  const [testimonialsCarouselApi, setTestimonialsCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [solidaireSlide, setSolidaireSlide] = useState(0);
  const [testimonialsSlide, setTestimonialsSlide] = useState(0);




  useEffect(() => {
    if (!carouselApi) return;
    carouselApi.on("select", () => setCurrentSlide(carouselApi.selectedScrollSnap()));
  }, [carouselApi]);

  useEffect(() => {
    if (!solidaireCarouselApi) return;
    solidaireCarouselApi.on("select", () => setSolidaireSlide(solidaireCarouselApi.selectedScrollSnap()));
  }, [solidaireCarouselApi]);

  useEffect(() => {
    if (!testimonialsCarouselApi) return;
    testimonialsCarouselApi.on("select", () => setTestimonialsSlide(testimonialsCarouselApi.selectedScrollSnap()));
  }, [testimonialsCarouselApi]);

  const featuredExcursions = excursions.filter((exc) => exc.tags?.coup_de_coeur && exc.is_published).slice(0, 6);
  const solidaireExcursions = excursions.filter((exc) => exc.tags?.impact_solidaire && exc.is_published).slice(0, 6);
  const topRatedTestimonials = [...testimonials].sort((a, b) => b.rating - a.rating).slice(0, 3);
  const featuredTestimonials = testimonials.slice(0, 6);

  return (
    <div>
      {/* Section Présentation */}
      <section
        id="presentation-section"

        className="py-12 md:py-32 animate-fade-in">
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <EditableImage
                src={siteImages['clea-portrait-homepage']?.image_url || "/clea-profile.webp"}
                alt="Cléa Carioca - Guide locale francophone à Rio de Janeiro"
                className="rounded-2xl w-full aspect-[3/4] object-cover max-w-sm mx-auto lg:max-w-none"
                loading="lazy"
                aspectRatio={3 / 4}
                bucket="site-assets"
                path="homepage/clea-portrait.jpg"
                onImageUpdate={async (newUrl) => {
                  await supabase.from('site_images').upsert({
                    image_key: 'clea-portrait-homepage',
                    image_url: newUrl,
                    category: 'homepage',
                    alt_text_fr: 'Cléa Carioca - Guide locale francophone à Rio de Janeiro'
                  }, { onConflict: 'image_key' });
                }} />
              
            </div>
            <div className="lg:col-span-3 space-y-10 order-1 lg:order-2">
              <div>
                <div className="w-12 h-px bg-roca-yellow mb-6" />
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight mb-6">
                  Votre guide locale francophone à Rio
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                  Je suis Cléa, française installée à Rio de Janeiro. Je vous accompagne à vivre des expériences locales et immersives, en toute sérénité. Loin des circuits touristiques classiques, je vous fais découvrir le Rio authentique que j'aime.
                </p>
                

                
              </div>

              <div className="border-t border-border pt-10">
                <div className="w-12 h-px bg-destructive mb-6" />
                <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">Un voyage qui a du sens</h3>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                  J'ai créé l'association <strong className="text-foreground">Meninos do Brasil</strong>, qui aide les communautés locales au Brésil. Certaines de mes excursions sont des <strong className="text-foreground">excursions solidaires</strong> dont une partie des revenus finance directement cette association.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Distribution de paniers alimentaires, matériel scolaire, activités éducatives et sportives pour les enfants, chaque réservation solidaire a un impact concret.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Excursions Coup de Cœur */}
      <section
        id="excursions-section"

        className="py-12 md:py-28 animate-fade-in" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="mb-10 md:mb-14">
            <div className="w-12 h-px bg-roca-yellow mb-6" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Mes excursions coup de cœur
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Les expériences les plus appréciées par mes voyageurs.
            </p>
          </div>

          <Carousel opts={{ align: "start", loop: true, slidesToScroll: 1 }} setApi={setCarouselApi} className="w-full mb-4 sm:mb-6">
            <CarouselContent className="-ml-2 md:-ml-4">
              {featuredExcursions.map((excursion) =>
              <CarouselItem key={excursion.id} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                  <div className="p-1">
                    <ExcursionCard
                    id={excursion.id} title={excursion.title} category={excursion.category}
                    tags={excursion.tags} resume={excursion.resume} debut={excursion.debut}
                    duree={excursion.duree} prix_eur={excursion.prix_eur}
                    participants_min={excursion.participants_min} participants_max={excursion.participants_max}
                    niveau={excursion.niveau} cover_image={excursion.cover_image} image={excursion.images?.[0]} />
                  
                  </div>
                </CarouselItem>
              )}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex" />
            <CarouselNext className="hidden sm:flex" />
          </Carousel>

          <div className="flex justify-center gap-2 mb-8 sm:hidden">
            {featuredExcursions.slice(0, 6).map((_, index) =>
            <button key={index} onClick={() => carouselApi?.scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-roca-orange' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'}`}
            aria-label={`Aller à l'excursion ${index + 1}`} />

            )}
          </div>

          <div className="mt-8">
            <Link to="/excursions" className="group inline-flex items-center gap-3 text-foreground font-medium hover:text-primary transition-colors">
              Voir toutes les excursions
              <span className="w-8 h-px bg-current group-hover:w-12 transition-all" />
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Excursions Solidaires */}
      <section
        id="solidaire-section"

        className="py-12 md:py-28 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="mb-10 md:mb-14">
            <div className="w-12 h-px bg-destructive mb-6" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Mes excursions solidaires
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Des visites à impact solidaire, dont une partie des revenus aide les communautés locales.
            </p>
          </div>

          {solidaireExcursions.length > 0 &&
          <>
              <Carousel opts={{ align: "start", loop: true, slidesToScroll: 1 }} setApi={setSolidaireCarouselApi} className="w-full mb-4 sm:mb-6">
                <CarouselContent className="-ml-2 md:-ml-4">
                  {solidaireExcursions.map((excursion) =>
                <CarouselItem key={excursion.id} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                      <div className="p-1">
                        <ExcursionCard
                      id={excursion.id} title={excursion.title} category={excursion.category}
                      tags={excursion.tags} resume={excursion.resume} debut={excursion.debut}
                      duree={excursion.duree} prix_eur={excursion.prix_eur}
                      participants_min={excursion.participants_min} participants_max={excursion.participants_max}
                      niveau={excursion.niveau} cover_image={excursion.cover_image} image={excursion.images?.[0]} />
                    
                      </div>
                    </CarouselItem>
                )}
                </CarouselContent>
                <CarouselPrevious className="hidden sm:flex" />
                <CarouselNext className="hidden sm:flex" />
              </Carousel>
              <div className="flex justify-center gap-2 mb-8 sm:hidden">
                {solidaireExcursions.slice(0, 6).map((_, index) =>
              <button key={index} onClick={() => solidaireCarouselApi?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${index === solidaireSlide ? 'w-8 bg-destructive' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'}`}
              aria-label={`Excursion solidaire ${index + 1}`} />

              )}
              </div>
            </>
          }

          <div>
            <a href="https://www.instagram.com/meninos.dobrasil" target="_blank" rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 text-foreground font-medium hover:text-destructive transition-colors">
              Découvrez l'impact
              <span className="w-8 h-px bg-current group-hover:w-12 transition-all" />
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Avis — "Ils ont voyagé avec Cléa" */}
      <section
        id="testimonials-section"

        className="py-12 md:py-28 animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="mb-10 md:mb-14">
            <div className="w-12 h-px bg-roca-orange mb-6" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
               Leurs mots, mon bonheur
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Témoignages de ceux qui ont vécu des expériences authentiques avec Roca Rio.
            </p>
          </div>

          {/* Mobile/Tablet: Carousel */}
          <div className="lg:hidden mb-6">
            <Carousel opts={{ align: "start", loop: true, slidesToScroll: 1 }} setApi={setTestimonialsCarouselApi} className="w-full">
              <CarouselContent className="-ml-2">
                {topRatedTestimonials.map((testimonial) =>
                <CarouselItem key={testimonial.id} className="pl-2 basis-full sm:basis-1/2">
                    <div className="p-1 h-full">
                      <TestimonialCard {...testimonial} compact={true} />
                    </div>
                  </CarouselItem>
                )}
              </CarouselContent>
            </Carousel>
            <div className="flex justify-center gap-2 mt-4">
              {topRatedTestimonials.map((_, index) =>
              <button key={index} onClick={() => testimonialsCarouselApi?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${index === testimonialsSlide ? 'w-8 bg-roca-orange' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'}`}
              aria-label={`Avis ${index + 1}`} />

              )}
            </div>
          </div>

          {/* Desktop: Grid 3 colonnes */}
          <div className="hidden lg:grid grid-cols-3 gap-6 mb-8">
            {topRatedTestimonials.map((testimonial, index) =>
            <div key={testimonial.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <TestimonialCard {...testimonial} compact={true} />
              </div>
            )}
          </div>

          <div className="mt-8">
            <Link to="/avis" className="group inline-flex items-center gap-3 text-foreground font-medium hover:text-roca-orange transition-colors">
              Lire tous les avis
              <span className="w-8 h-px bg-current group-hover:w-12 transition-all" />
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Final — Card tilted */}
      <section
        id="cta-section"

        className="py-20 md:py-32 animate-fade-in" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <div className="relative max-w-2xl w-full">
            {/* Tilted card */}
            <div className="bg-roca-blue-dark rounded-2xl shadow-float p-10 sm:p-14 md:p-16 text-center rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                Prêt à explorer Rio ?
              </h2>
              <p className="text-white/70 text-lg mb-10">
                Discutez directement avec Cléa pour organiser votre aventure.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white text-base h-12 px-8 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                
                <a
                  href="https://wa.me/5521976795050?text=Bonjour%20Cléa%2C%20je%20souhaite%20en%20savoir%20plus%20sur%20vos%20excursions%20!"
                  target="_blank"
                  rel="noopener noreferrer">
                  
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Écrire à Cléa
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>);

};